import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { fetchLoanReports } from "../redux/slices/loanReportSlice";
import { fetchLoanEmis, clearLoanEmis } from "../redux/slices/LoanEmiSlice";
import html2pdf from "html2pdf.js";
import "../css/LoanAgreementPage.css";

const formatDate = (date) => {
  if (!date) return "N/A";
  return new Date(date).toLocaleDateString("en-GB");
};

const formatAmount = (amount) => {
  if (!amount && amount !== 0) return "N/A";
  return `₹${Number(amount).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
  })}`;
};

const LoanAgreementPage = () => {
  const { loanId } = useParams();
  const dispatch = useDispatch();
  const [company, setCompany] = useState(null);

  const {
    loans,
    loading: loansLoading,
    error: loansError,
  } = useSelector((state) => state.loanReport);
  const {
    emis,
    loading: emisLoading,
    error: emisError,
  } = useSelector((state) => state.loanEmi);

  const superAdminId = Number(localStorage.getItem("adminuserId")) || 0;

  useEffect(() => {
    dispatch(fetchLoanReports());
    dispatch(fetchLoanEmis(loanId));

    // Fetch company details dynamically
    const fetchCompany = async () => {
      if (!superAdminId) return;
      try {
        const res = await fetch(
          `https://narrowitsolution.in/api/get-company?superAdminId=${superAdminId}`
        );
        const data = await res.json();
        if (res.ok && data) {
          if (data.logo) {
            data.logoUrl = `https://narrowitsolution.in/${data.logo.replace(/^images\//, "")}`;
          }
          setCompany(data);
        }
      } catch (err) {
        console.error("Error fetching company details:", err);
      }
    };

    fetchCompany();

    return () => {
      dispatch(clearLoanEmis());
    };
  }, [dispatch, loanId, superAdminId]);

  const loan =
    loans?.find(
      (l) =>
        String(l.id) === String(loanId) ||
        String(l.loanId) === String(loanId) ||
        String(l.loanAccountNumber) === String(loanId)
    ) || null;

  const emiAmountFromSchedule = emis?.[0]?.emiAmount || 0;

  if (loansLoading || emisLoading)
    return <p className="LoanAgreementPage-loading">Loading...</p>;
  if (loansError)
    return <p className="LoanAgreementPage-error">Error: {loansError}</p>;
  if (!loan)
    return (
      <p className="LoanAgreementPage-error">
        No loan found for Loan ID {loanId}
      </p>
    );

  const handleDownloadPDF = () => {
    const element = document.getElementById("loan-agreement");
    const opt = {
      margin: [20, 15, 20, 15],
      filename: `Loan_Agreement_${loan.loanAccountNumber || loan.id}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      pagebreak: { mode: ["avoid-all", "css", "legacy"] },
    };
    html2pdf().set(opt).from(element).save();
  };

  return (
    <div className="LoanAgreementPage-wrapper">
      <div className="LoanAgreementPage-downloadBtnWrapper">
        <button
          className="LoanAgreementPage-downloadBtn"
          onClick={handleDownloadPDF}
        >
          Download PDF
        </button>
      </div>

      <div className="LoanAgreementPage-container" id="loan-agreement">
        {/* Custom header for PDF pages */}
        <div className="pdf-header">
          <p>{company?.name || "TechVision Finance Pvt. Ltd."} | Loan Agreement</p>
        </div>

        {/* Actual Agreement */}
        <section className="LoanAgreementPage-section LoanAgreementPage-header">
          <div className="LoanAgreementPage-header-left">
            {company?.logoUrl && (
              <img
                src={company.logoUrl}
                alt="Company Logo"
                className="LoanAgreementPage-logo"
              />
            )}
            <div>
              <h1 className="LoanAgreementPage-companyName">
                {company?.name || "TechVision Finance Pvt. Ltd."}
              </h1>
              <p className="LoanAgreementPage-companyDetails">
                {company?.address || "123 Business Park, Bangalore, India"} <br />
                Phone: {company?.contact || "+91 98765 43210"} | Email:{" "}
                {company?.email || "support@techvision.com"}
              </p>
            </div>
          </div>
          <div className="LoanAgreementPage-header-right">
            <h2>Loan Agreement</h2>
            <p>
              Agreement ID: {loan.loanAccountNumber || loan.loanId || loan.id}
            </p>
          </div>
        </section>

        {/* Borrower & Loan Info */}
        <section className="LoanAgreementPage-section LoanAgreementPage-infoCard">
          <h3>Customer Information</h3>
          <div className="LoanAgreementPage-infoGrid">
            <p>
              <strong>Name:</strong> {loan.userName || "N/A"}
            </p>
            <p>
              <strong>Guardian:</strong> {loan.guardianName || "N/A"}
            </p>
            <p>
              <strong>Contact:</strong> {loan.mobileNo || "N/A"}
            </p>
          </div>

          <h3>Loan Information</h3>
          <div className="LoanAgreementPage-infoGrid">
            <p>
              <strong>Loan ID:</strong>{" "}
              {loan.loanAccountNumber || loan.loanId || loan.id}
            </p>
            <p>
              <strong>Type:</strong> {loan.loanType || "N/A"}
            </p>
            <p>
              <strong>Applied Date:</strong> {formatDate(loan.loanAppliedDate)}
            </p>
            <p>
              <strong>Status:</strong> {loan.status || "N/A"}
            </p>
            <p>
              <strong>Loan Amount:</strong> {formatAmount(loan.loanAmount)}
            </p>
            <p>
              <strong>EMI Amount:</strong> {formatAmount(emiAmountFromSchedule)}
            </p>
            <p>
              <strong>Exit Amount:</strong> {formatAmount(loan.loanPayable)}
            </p>
          </div>
        </section>

        {/* EMI Schedule */}
        <section className="LoanAgreementPage-section LoanAgreementPage-emiCard">
          <h3>EMI Schedule</h3>
          {emisError ? (
            <p className="LoanAgreementPage-error">Error: {emisError}</p>
          ) : emis.length === 0 ? (
            <p className="LoanAgreementPage-noData">No EMI records found.</p>
          ) : (
            <table className="LoanAgreementPage-table">
              <thead>
                <tr>
                  <th>SN</th>
                  <th>Due Date</th>
                  <th>Principal</th>
                  <th>Interest</th>
                  <th>EMI Amount</th>
                </tr>
              </thead>
              <tbody>
                {emis.map((emi, index) => (
                  <tr key={emi.id}>
                    <td>{index + 1}</td>
                    <td>{formatDate(emi.dueDate)}</td>
                    <td>{formatAmount(emi.principalComponent)}</td>
                    <td>{formatAmount(emi.interestComponent)}</td>
                    <td>{formatAmount(emi.emiAmount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* Agreement Terms */}
        <section className="LoanAgreementPage-section LoanAgreementPage-terms page-break">
          <h3>Agreement Terms & Conditions</h3>
          <p>
            This loan agreement is made between{" "}
            <strong>{company?.name || "TechVision Finance Pvt. Ltd."}</strong> and the borrower. The borrower agrees to repay the loan in accordance with the EMI schedule provided.
          </p>
          {/* keep your terms text here ... */}
        </section>

        {/* Signatures */}
        <section className="LoanAgreementPage-section LoanAgreementPage-signatures page-break">
          <div>
            <p>________________________</p>
            <p>
              <strong>Authorized Signatory</strong>
            </p>
          </div>
          <div>
            <p>________________________</p>
            <p>
              <strong>Borrower</strong>
            </p>
          </div>
        </section>

        {/* Footer for every PDF page */}
        <div className="pdf-footer">
          <p>{company?.name || "TechVision Finance Pvt. Ltd."} | Confidential | Page </p>
        </div>
      </div>
    </div>
  );
};

export default LoanAgreementPage;
